import type { AnalysisConfig, HintThresholds } from './types';

/** Erstes Jahr der historischen Analyse (Auftrag F03). */
export const FIRST_YEAR = 1991;

/**
 * Letztes vollständig verfügbares Kalenderjahr. ERA5/ERA5-Land erscheinen bei
 * Open-Meteo mit rund fünf Tagen Verzug; die Nacht vom 31. Dezember braucht
 * zudem Werte bis 1. Januar 06:00 Ortszeit. Sicherheitsmarge: 10. Januar.
 */
export function lastCompleteYear(now: Date = new Date()): number {
  const y = now.getUTCFullYear();
  const cutoff = Date.UTC(y, 0, 10);
  return now.getTime() >= cutoff ? y - 1 : y - 2;
}

export function defaultConfig(now: Date = new Date()): AnalysisConfig {
  return {
    model: 'era5_land',
    heightCorrection: true,
    hddBase: 18,
    cddBase: 22,
    hotDayThreshold: 30,
    heatPeriodMinDays: 3,
    nightStartHour: 22,
    nightEndHour: 6,
    warmNightThreshold: 20,
    coolingRef: 26,
    coolingMinDelta: 2,
    coolingSeasonStartMonth: 5,
    coolingSeasonEndMonth: 9,
    referenceStart: 1991,
    referenceEnd: 2020,
    periodStart: FIRST_YEAR,
    periodEnd: lastCompleteYear(now),
    hints: defaultHints(),
  };
}

export function defaultHints(): HintThresholds {
  return {
    recentYears: 10,
    cddRecentMin: 20,
    hotDaysRecentMin: 5,
    coolShareMin: 0.6,
    warmNightsRecentMin: 5,
    heatLongestMin: 5,
    hddDropMin: 0.1,
    cddRiseMin: 0.5,
  };
}

/** Konstanter vertikaler Temperaturgradient der Höhenkorrektur (K/m). */
export const LAPSE_RATE = 0.0065;

export interface ParamSpec {
  key: keyof AnalysisConfig;
  min: number;
  max: number;
  step: number;
  unit: string;
}

export const PARAM_SPECS: ParamSpec[] = [
  { key: 'hddBase', min: 10, max: 22, step: 0.5, unit: '°C' },
  { key: 'cddBase', min: 16, max: 28, step: 0.5, unit: '°C' },
  { key: 'hotDayThreshold', min: 25, max: 35, step: 0.5, unit: '°C' },
  { key: 'heatPeriodMinDays', min: 2, max: 7, step: 1, unit: 'Tage' },
  { key: 'warmNightThreshold', min: 15, max: 25, step: 0.5, unit: '°C' },
  { key: 'coolingRef', min: 20, max: 30, step: 0.5, unit: '°C' },
  { key: 'coolingMinDelta', min: 0, max: 8, step: 0.5, unit: 'K' },
  { key: 'nightStartHour', min: 18, max: 23, step: 1, unit: 'h' },
  { key: 'nightEndHour', min: 4, max: 9, step: 1, unit: 'h' },
  { key: 'coolingSeasonStartMonth', min: 1, max: 12, step: 1, unit: 'Monat' },
  { key: 'coolingSeasonEndMonth', min: 1, max: 12, step: 1, unit: 'Monat' },
];

/** Klemmt numerische Parameter auf zulässige Bereiche und prüft Zeiträume. */
export function sanitizeConfig(c: AnalysisConfig, now: Date = new Date()): AnalysisConfig {
  const out: AnalysisConfig = { ...c };
  for (const s of PARAM_SPECS) {
    const v = Number(out[s.key]);
    (out as unknown as Record<string, number>)[s.key] = Number.isFinite(v) ? Math.min(s.max, Math.max(s.min, v)) : (defaultConfig(now)[s.key] as number);
  }
  const last = lastCompleteYear(now);
  out.periodStart = Math.max(FIRST_YEAR, Math.min(out.periodStart, last));
  out.periodEnd = Math.max(out.periodStart, Math.min(out.periodEnd, last));
  out.referenceStart = 1991;
  out.referenceEnd = 2020;
  const h = { ...defaultHints(), ...(c.hints ?? {}) };
  const clamp = (v: number, lo: number, hi: number, d: number) => (Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);
  const d = defaultHints();
  out.hints = {
    recentYears: Math.round(clamp(h.recentYears, 3, 20, d.recentYears)),
    cddRecentMin: clamp(h.cddRecentMin, 0, 500, d.cddRecentMin),
    hotDaysRecentMin: clamp(h.hotDaysRecentMin, 0, 100, d.hotDaysRecentMin),
    coolShareMin: clamp(h.coolShareMin, 0, 1, d.coolShareMin),
    warmNightsRecentMin: clamp(h.warmNightsRecentMin, 0, 200, d.warmNightsRecentMin),
    heatLongestMin: Math.round(clamp(h.heatLongestMin, 2, 30, d.heatLongestMin)),
    hddDropMin: clamp(h.hddDropMin, 0, 1, d.hddDropMin),
    cddRiseMin: clamp(h.cddRiseMin, 0, 5, d.cddRiseMin),
  };
  if (out.nightStartHour <= out.nightEndHour) {
    out.nightStartHour = 22;
    out.nightEndHour = 6;
  }
  return out;
}
